import { el } from "@dhx/ts-common/dom";
import { getWidth } from "../helpers/cells";
import { transpose, isSortable } from "../helpers/main";
import { GridEvents, ICol, IFixedRowsConfig, IRendererConfig, GridSystemEvents } from "../types";

const BORDERS = 2;

function handleMouse(col: ICol, config: IRendererConfig, type: string, e: any) {
	if (!type) return;
	const systemEvent = type.toLocaleLowerCase().includes("touch");
	if (systemEvent) {
		config._events.fire(type, [col, e]);
	} else {
		config.events.fire(type, [col, e]);
	}
}

function getHandlers(column: ICol, rowName: string, config: IRendererConfig) {
	return {
		onclick: [handleMouse, column, config, GridEvents[rowName + "CellClick"]],
		onmouseover: [handleMouse, column, config, GridEvents[rowName + "CellMouseOver"]],
		onmousedown: [handleMouse, column, config, GridEvents[rowName + "CellMouseDown"]],
		ontouchstart: [handleMouse, column, config, GridEvents[rowName + "CellMouseDown"]],
		ondblclick: [handleMouse, column, config, GridEvents[rowName + "CellDblClick"]],
		oncontextmenu: [handleMouse, column, config, GridEvents[rowName + "CellRightClick"]],
		ontouchmove: [handleMouse, column, config, GridSystemEvents[rowName + "CellTouchMove"]],
		ontouchend: [handleMouse, column, config, GridSystemEvents[rowName + "CelltouchEnd"]],
	};
}

function buildRows(columns: ICol[], name: string): any[][] {
	const header = columns.map(col => col[name] || [{}]);
	return transpose(header);
}

function getCustomContentCell(
	cell: any,
	column: ICol,
	config: IRendererConfig,
	rowName: string,
	css = "",
	colIndex: number,
	rowIndex: number
) {
	const type = column.type ? `dhx_${column.type}-cell` : "dhx_string-cell";
	// TODO: over with index of filter inside of header or footer
	const content = config.content[cell.content] && config.content[cell.content].toHtml(column, config);
	const cellRow = {};
	config.columns.forEach(col => {
		const hasContent = !!config.content[col[rowName][rowIndex].content];
		cellRow[col.id] =
			(hasContent && config.content[col[rowName][rowIndex].content].toHtml(col, config)) ||
			col[rowName][rowIndex].text;
	});
	return el(
		`.${type}`,
		{
			style: {
				class: css.trim(),
				padding: 0,
			},
		},
		[
			content &&
				(typeof content === "string" || typeof content === "number"
					? el("div", {
							class: "dhx_grid-footer-cell-text",
							role: "presentation",
							".innerHTML":
								column.template && typeof content === "string"
									? column.template(content, cellRow, column)
									: content,
					  })
					: content),
		]
	);
}

export function getRows(config: IRendererConfig, rowsConfig: IFixedRowsConfig) {
	if (!config.data || !config.columns) {
		return [];
	}
	const getRowAriaAttrs = ind => ({
		role: "row",
		"aria-rowindex": ind + 1,
	});
	const pos = config.$positions;
	const rowName = rowsConfig.name;
	const columns = config.currentColumns;
	const rowHeight = config[`${rowName}RowHeight`] || 40;
	const rows = buildRows(columns, rowName);

	return rows.map((row, j) =>
		el(
			`.dhx_${rowName}-row`,
			{
				style: {
					height: rowHeight,
				},
				...getRowAriaAttrs(j),
			},
			row.map((cell, i) => {
				let css = cell.css || "";
				const column = columns[i];
				const colIndex = pos.xStart + i + 1;
				let sortIconCss = "dxi dxi-sort-variant dhx_grid-sort-icon";
				let ariaSort = "none";

				if (config.sortBy && `${column.id}` === config.sortBy && !cell.content) {
					const dir = config.sortDir || "asc";
					sortIconCss += ` dhx_grid-sort-icon--${dir}`;
					css += ` dhx_grid-${rowName}-cell--sorted `;
					ariaSort = dir === "asc" ? "ascending" : "descending";
				}

				const sortIconVisible =
					isSortable(config, column) &&
					cell.text &&
					rowName !== "footer" &&
					cell.headerSort !== false;
				if (sortIconVisible) {
					css += " dhx_grid-header-cell--sortable";
				}

				const isFirstCol = i === 0 ? "dhx_first-column-cell" : "";

				const isLastCol = i === columns.length - 1 ? "dhx_last-column-cell" : "";

				if (!cell.content) {
					if (cell.align) {
						css += ` dhx_grid-header-cell--align_${cell.align} `;
					} else {
						css += ` dhx_grid-header-cell--${
							column.type === "number" || column.type === "percent" || column.type === "date"
								? "align_right"
								: "align_left"
						} `;
					}
				}

				css += `${isFirstCol} ${isLastCol}`;

				let resizable: any = column.resizable !== undefined ? column.resizable : config.resizable;
				if (resizable) {
					resizable = el(
						"div",
						{
							class: "dhx_resizer_grip_wrap",
							"aria-hidden": "true",
						},
						[
							el(
								"div",
								{
									class: "dhx_resizer_grip",
									dhx_resized: column.id,
									style: {
										height: `${rows.length * 100}%`,
									},
								},
								[el("div", { class: "dhx_resizer_grip_line" })]
							),
						]
					);
					if (rowName === "footer" || j > 0) {
						resizable = null;
					}
				}

				if (cell.align) {
					css += ` dhx_align-${cell.align}`;
				}

				const getCellAriaAttrs = (rowName, colIndex, ariaSort) => {
					const attrs = {
						"aria-colindex": colIndex,
					};

					if (!sortIconVisible) {
						// if cell is sortable - set tabindex on button (getInnerCellAriaAttrs),
						// else - on cell
						// attrs["tabindex"] = 0;
						// TODO: after sort AND if first cell is not visible: selection.setCell(first cell) -> scrollTo
						// TODO: use arrows to navigate, set tabindex for first cell only
					}

					if (rowName === "footer" || cell.content) {
						attrs["role"] = "gridcell";
					} else {
						attrs["role"] = "columnheader";
						attrs["aria-sort"] = ariaSort;
						// attrs["aria-label"] = title;
					}
					return attrs;
				};

				if (cell.content) {
					return el(
						`.dhx_grid-${rowName}-cell.dhx_grid-custom-content-cell`,
						{
							class: css.trim(),
							dhx_id: column.id,
							_key: i,
							style: {
								width: column.$width,
								height:
									rowName === "footer" ? rowHeight + BORDERS / 2 + "px" : rowHeight + "px",
							},
							...getHandlers(column, rowName, config),
							...getCellAriaAttrs(rowName, colIndex, ariaSort),
						},
						[
							getCustomContentCell(cell, column, config, rowName, "", colIndex, j),
							resizable || null,
						]
					);
				}

				const getInnerCellAriaAttrs = (rowName, text) => {
					return sortIconVisible
						? {
								role: "button",
								"aria-label": `Sort by ${text}`,
								// tabindex: 0,
								// TODO: after sort AND if first cell is not visible: selection.setCell(first cell) -> scrollTo
								// TODO: use arrows to navigate, set tabindex for first cell only
						  }
						: {};
				};

				let cellCss = "dhx_grid-header-cell-text_content";
				if (config.autoHeight) cellCss += " dhx_grid-header-cell-text_content-auto-height";

				return el(
					`.dhx_grid-${rowName}-cell`,
					{
						class: css.trim(),
						dhx_id: column.id,
						_key: i,
						style: {
							width: column.$width,
							height: rowName === "footer" ? rowHeight + BORDERS / 2 + "px" : rowHeight + "px",
						},
						...getHandlers(column, rowName, config),
						...getCellAriaAttrs(rowName, colIndex, ariaSort),
					},
					[
						el(
							"div.dhx_grid-header-cell-text",
							{
								role: "presentation",
							},
							[
								el("span", {
									class: cellCss,
									...getInnerCellAriaAttrs(rowName, cell.text),
									style: { lineHeight: `${rowHeight}px` },
									".innerHTML": cell.text,
								}),
								resizable || null,
							]
						),
						sortIconVisible && el("div", { class: sortIconCss, "aria-hidden": "true" }),
					]
				);
			})
		)
	);
}

export function getFixedSpans(config: IRendererConfig, rowsConfig: IFixedRowsConfig) {
	const cols = config.columns;
	const rows = transpose(cols.map(col => col[rowsConfig.name] || []));
	const height = config[`${rowsConfig.name}RowHeight`] || 40;
	const rowName = rowsConfig.name;
	const pos = config.$positions;

	let leftShift = 0;
	return rows.map((row, i) => {
		leftShift = 0;
		return el(
			".dhx_span-row",
			{
				style: { top: height * i + "px", height },
				class: "dhx_header-row",
				"aria-hidden": "true",
			},
			row
				.map((cell, cellIdx) => {
					const col = cols[cellIdx];
					const colIndex = pos.xStart + cellIdx + 1;

					leftShift += col.hidden ? 0 : col.$width;
					const isFirstCol = cellIdx === 0 ? "dhx_first-column-cell" : "";

					const isLastCol =
						cellIdx === cols.length - 1 || (cell.colspan || 0) + (cellIdx - 1) >= cols.length - 1
							? "dhx_last-column-cell"
							: "";
					let spanHeight = height;

					if (cell.rowspan) {
						spanHeight = spanHeight * cell.rowspan - 1;
					}

					const sortIconVisible =
						isSortable(config, col) && cell.rowspan && cell.text && rowsConfig.name !== "footer";

					let sortIconCss = "dxi dxi-sort-variant dhx_grid-sort-icon";

					if (config.sortBy && `${col.id}` === config.sortBy && !cell.content) {
						sortIconCss += ` dhx_grid-sort-icon--${config.sortDir || "asc"}`;
					}

					const cellAlign = col.align
						? `dhx_align-${col.align}`
						: (col.type === "number" || col.type === "percent" || col.type === "date") &&
						  !cell.colspan
						? "dhx_align-right"
						: "dhx_align-left";

					let css = `dhx_grid-header-cell ${isFirstCol} ${isLastCol} ${
						cell.rowspan ? "dhx_span-cell__rowspan" : ""
					} ${cell.align ? `dhx_align-${cell.align}` : cellAlign} ${cell.css ? cell.css : ""}`;

					if (sortIconVisible) {
						css += " dhx_grid-header-cell--sortable";
					}

					if (!cell.content) {
						if (cell.align) {
							css += ` dhx_grid-header-cell--align_${cell.align} `;
						} else {
							css += ` dhx_grid-header-cell--${
								col.type === "number" || col.type === "percent" || col.type === "date"
									? "align_right"
									: "align_left"
							} `;
						}
					}
					let content = null;
					if (cell.content) {
						content = getCustomContentCell(cell, col, config, rowName, css, colIndex, i);
						content.attrs.style = {
							...content.attrs.style,
							width: "100%",
							borderRight: "0",
						};
					}
					let borderLeft = "";
					if (leftShift - col.$width > 0) {
						borderLeft = "1px solid #e4e4e4";
					}

					let cellCss = "dhx_grid-header-cell-text_content";
					if (config.autoHeight) cellCss += " dhx_grid-header-cell-text_content-auto-height";

					return cell.colspan || cell.rowspan
						? el(
								".dhx_span-cell",
								{
									style: {
										width: getWidth(cols, cell.colspan, cellIdx),
										height: rowName === "footer" ? spanHeight + BORDERS / 2 : spanHeight,
										left: leftShift - col.$width,
										borderLeft,
										top: height * i,
									},
									class: css.trim(),
									dhx_id: col.id,
									...getHandlers(col, rowName, config),
								},
								[
									content || cell.rowspan
										? el(
												"div.dhx_grid-header-cell-text",
												{
													role: "presentation",
												},
												[
													el("span", {
														class: cellCss,
														".innerHTML": cell.text,
													}),
												]
										  )
										: el("span", {
												class: cellCss,
												".innerHTML": cell.text,
										  }),
									sortIconVisible && el("div", { class: sortIconCss }),
								]
						  )
						: null;
				})
				.filter(cell => cell)
		);
	});
}

export function getFixedRows(config: IRendererConfig, rowsConfig: IFixedRowsConfig) {
	const rows = getRows(config, rowsConfig);
	const spans = getFixedSpans(config, rowsConfig);
	const getRowAriaAttrs = rowCount => ({
		role: "rowgroup",
		"aria-rowcount": rowCount,
	});
	let fixedCols = null;
	if (rowsConfig.name === "footer" && !rowsConfig.sticky) {
		fixedCols =
			config.leftSplit >= 0 &&
			getRows(
				{
					...config,
					currentColumns: config.columns.slice(0, config.leftSplit),
					$positions: { ...config.$positions, xStart: 0, xEnd: config.leftSplit },
				},
				rowsConfig
			);
	}
	const styles = {
		position: "sticky",
		[rowsConfig.position]: 0,
	};
	let left;
	if (!rowsConfig.sticky) {
		styles.left = -config.scroll.left;
		left = -config.scroll.left;
		styles.position = "relative";
	}
	return el(
		`.dhx_${rowsConfig.name}-wrapper`,
		{
			class: rowsConfig.sticky ? "" : `dhx_compatible-${rowsConfig.name}`,
			style: {
				...styles,
				left: rowsConfig.sticky ? left : 0,
				height:
					rowsConfig.name === "footer"
						? config[rowsConfig.name + "Height"] + BORDERS / 2
						: config[rowsConfig.name + "Height"],
				width: rowsConfig.sticky ? config.$totalWidth : rowsConfig.wrapper.width - BORDERS,
			},
			role: "presentation",
		},
		[
			el(
				`.dhx_grid-${rowsConfig.name}`,
				{
					style: {
						height:
							rowsConfig.name === "footer"
								? config[rowsConfig.name + "Height"] + BORDERS / 2
								: config[rowsConfig.name + "Height"],
						left,
						paddingLeft: rowsConfig.shifts.x,
						width: config.$totalWidth,
					},
					role: "presentation",
				},
				[
					el(
						`.dhx_${rowsConfig.name}-rows`,
						{
							...getRowAriaAttrs([...rows].length),
						},
						[...rows]
					),
					el(
						`.dhx_${rowsConfig.name}-spans`,
						{
							style: {
								marginLeft: -rowsConfig.shifts.x,
							},
							class: `dhx_${rowsConfig.name}-rows`,
							role: "presentation",
						},
						spans
					),
					fixedCols &&
						el(
							`.dhx_${rowsConfig.name}-fixed-cols`,
							{
								style: {
									position: "absolute",
									top: 0,
									left: config.scroll.left + "px",
									height: "100%",
								},
							},
							fixedCols
						),
				]
			),
			el("div", { style: { width: config.$totalWidth }, role: "presentation" }),
		]
	);
}
